'use client';

import { CLIENT_IMAGE_META_FIELDS } from '@/ai/image/client-meta';
import { buildClientImageMeta } from '@/ai/image/client/client-image-meta';
import { uploadEnhancerInputFile } from '@/ai/image/client/upload-input-file';
import {
  WORKSPACE_UPLOAD_FOLDER,
  resolveWorkspaceAcceptedUploadTypes,
} from '@/cms/components/blocks/workspace/workspace-1/actions/runtime-utils';
import { dispatchCmsWorkspaceLaunch } from '@/cms/components/blocks/workspace/workspace-session-controller';
import { loadWorkspaceToolModel } from '@/cms/components/blocks/workspace/workspace-tool-resource';
import { useAuthSessionObserver } from '@/lib/auth/auth-session-observer';
import { toast } from '@/lib/ui/toast';
import { isPaymentResultToken } from '@/payment/result-token';
import {
  cleanupExpiredWorkspaceTaskLocalFiles,
  deleteWorkspaceTaskLocalFileSlot,
  readWorkspaceTaskLocalFiles,
  stageWorkspaceTaskLocalFiles,
} from '@/payment/task/task-local-files';
import {
  type WorkspaceTaskResumeResult,
  WorkspaceTaskUploadBindError,
  bindWorkspaceTaskUpload,
  findLatestResumableWorkspaceTaskPayment,
  forgetGuestTaskCheckoutRecovery,
  readLatestGuestTaskCheckoutRecovery,
  rememberGuestTaskCheckoutRecovery,
  resumeWorkspaceTaskPayment,
} from '@/payment/task/task-payment-client';
import { siteRuntimeTaskPaymentResumeCopy } from '@/site-runtime/code-owned/task-payment-resume-copy';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

export type WorkspaceTaskResumeRequest = {
  pendingRunId: string;
  paymentIntentId: string;
  guestRecoveryToken?: string;
};

export type WorkspaceTaskResumeFeedback = {
  tone: 'success' | 'info' | 'error';
  message: string;
};

const inFlightResumes = new Map<string, Promise<WorkspaceTaskResumeResult>>();

function requestWorkspaceTaskResumeOnce(
  request: WorkspaceTaskResumeRequest,
  userId: string
): Promise<WorkspaceTaskResumeResult> {
  const key = `${userId}:${request.pendingRunId}:${request.paymentIntentId}:${request.guestRecoveryToken ?? ''}`;
  const existing = inFlightResumes.get(key);
  if (existing) {
    return existing;
  }
  const pending = resumeWorkspaceTaskPayment(request).finally(() => {
    inFlightResumes.delete(key);
  });
  inFlightResumes.set(key, pending);
  return pending;
}

/**
 * Reads the trusted payment result token from the Workspace URL. Only the
 * exact `pr_` run id plus a well-formed Billing payment intent token is a
 * lookup request; a cancel callback only carries `pending_run_id` and stays
 * inert. The server re-verifies ownership and payment facts.
 */
export function resolveWorkspaceTaskResumeRequest(
  href: string | null | undefined
): WorkspaceTaskResumeRequest | null {
  if (!href) {
    return null;
  }
  const url = new URL(href, 'https://workspace-resume.invalid');
  const pendingRunId = url.searchParams.get('pending_run_id')?.trim() ?? '';
  const paymentIntentId =
    url.searchParams.get('payment_intent_id')?.trim() ?? '';
  const guestRecoveryToken =
    url.searchParams.get('guest_recovery_token')?.trim() ?? '';
  if (
    !pendingRunId.startsWith('pr_') ||
    !isPaymentResultToken(paymentIntentId)
  ) {
    return null;
  }
  return {
    pendingRunId,
    paymentIntentId,
    ...(guestRecoveryToken ? { guestRecoveryToken } : {}),
  };
}

export function resolveWorkspaceTaskResumeFeedback(
  result: WorkspaceTaskResumeResult
): WorkspaceTaskResumeFeedback {
  const copy = siteRuntimeTaskPaymentResumeCopy;
  switch (result.status) {
    case 'submitted':
      return { tone: 'success', message: copy.submitted };
    case 'already_submitted':
      return { tone: 'success', message: copy.alreadySubmitted };
    case 'needs_local_files':
      return { tone: 'info', message: copy.needsLocalFiles };
    case 'blocked':
      return {
        tone: 'error',
        message: result.error?.trim() ? result.error : copy.blockedFallback,
      };
  }
}

/**
 * Drops the resume lookup parameters after the server returns a terminal
 * result so the feedback/toast cannot fire again, while preserving unrelated
 * query parameters and the hash (for example `?tool=...`).
 */
export function clearWorkspaceTaskResumeResultParams(href: string): string {
  const url = new URL(href, 'https://workspace-resume.invalid');
  url.searchParams.delete('pending_run_id');
  url.searchParams.delete('payment_intent_id');
  url.searchParams.delete('guest_recovery_token');
  return `${url.pathname}${url.search}${url.hash}`;
}

function clearGuestRecoveryTokenFromLocation() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('guest_recovery_token')) return;
  url.searchParams.delete('guest_recovery_token');
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
}

function resolveWorkspaceTaskOwnerKey(
  result: WorkspaceTaskResumeResult,
  fallback: string
) {
  return result.ownerType === 'guest' ? 'guest' : fallback;
}

export function resetWorkspaceTaskPaymentResumeForTests() {
  inFlightResumes.clear();
  localFileRecoveries.clear();
}

export type WorkspaceTaskLocalRecoveryOutcome =
  | { status: 'completed'; result: WorkspaceTaskResumeResult }
  | { status: 'missing_files' }
  | { status: 'retry_later' };

const localFileRecoveries = new Map<
  string,
  Promise<WorkspaceTaskLocalRecoveryOutcome>
>();

function resolveMissingInputSlotIds(
  result: WorkspaceTaskResumeResult
): string[] {
  if (result.missingInputSlotIds?.length) {
    return result.missingInputSlotIds;
  }
  return (result.expectedInputSlots ?? []).map((slot) => slot.inputSlotId);
}

/**
 * Uploads every locally staged file whose slot is still missing on the
 * server, binds it to the paid pending run, and only then deletes that local
 * blob. A failed upload or bind keeps the remaining blobs and the payment
 * token so a refresh can retry; a slot whose local file is gone is never
 * claimed as complete.
 */
async function bindMissingWorkspaceTaskLocalFileSlots(params: {
  request: WorkspaceTaskResumeRequest;
  userId: string;
  missingInputSlotIds: string[];
}): Promise<'bound' | 'missing_files' | 'retry_later'> {
  const stored = await readWorkspaceTaskLocalFiles({
    pendingRunId: params.request.pendingRunId,
    userId: params.userId,
  });
  if (!stored) {
    return 'missing_files';
  }
  const fileBySlotId = new Map(
    stored.files.map(({ inputSlotId, file }) => [inputSlotId, file])
  );
  const slotIdsToUpload = params.missingInputSlotIds.filter((inputSlotId) =>
    fileBySlotId.has(inputSlotId)
  );
  for (const inputSlotId of slotIdsToUpload) {
    const file = fileBySlotId.get(inputSlotId);
    if (!file) continue;
    let uploadResult: Awaited<ReturnType<typeof uploadEnhancerInputFile>>;
    let clientImageMeta: Awaited<ReturnType<typeof buildClientImageMeta>>;
    try {
      uploadResult = await uploadEnhancerInputFile({
        file,
        folder: WORKSPACE_UPLOAD_FOLDER,
        ...(params.userId === 'guest'
          ? {
              guestTask: {
                pendingRunId: params.request.pendingRunId,
                inputSlotId,
                paymentIntentId: params.request.paymentIntentId,
                ...(params.request.guestRecoveryToken
                  ? { guestRecoveryToken: params.request.guestRecoveryToken }
                  : {}),
              },
            }
          : {}),
        maxBytes: stored.uploadContract.maxBytes,
        supportedMimeTypes: stored.uploadContract.supportedMimeTypes,
        ...(stored.uploadContract.imageDimensionLimits
          ? {
              imageDimensionLimits: stored.uploadContract.imageDimensionLimits,
            }
          : {}),
      });
      // Upload preprocessing may normalize format and dimensions. The bound
      // metadata must describe the file that was actually stored, and only
      // the four browser-trusted fields can enter the shared runtime schema.
      clientImageMeta = await buildClientImageMeta({
        toolId: stored.toolId,
        file: uploadResult.uploadedFile,
        requirements: { fields: CLIENT_IMAGE_META_FIELDS },
      });
    } catch {
      return 'retry_later';
    }
    if (!clientImageMeta) {
      return 'retry_later';
    }
    try {
      await bindWorkspaceTaskUpload({
        pendingRunId: params.request.pendingRunId,
        paymentIntentId: params.request.paymentIntentId,
        inputSlotId,
        storageUrl: uploadResult.url,
        clientImageMeta: {
          width: clientImageMeta.width,
          height: clientImageMeta.height,
          format: clientImageMeta.format,
          hasTransparentPixels: clientImageMeta.hasTransparentPixels,
        },
        ...(params.request.guestRecoveryToken
          ? { guestRecoveryToken: params.request.guestRecoveryToken }
          : {}),
      });
    } catch (error) {
      if (
        error instanceof WorkspaceTaskUploadBindError &&
        error.errorCode === 'TASK_UPLOAD_BIND_SLOT_CONFLICT'
      ) {
        return 'missing_files';
      }
      return 'retry_later';
    }
    try {
      await deleteWorkspaceTaskLocalFileSlot({
        pendingRunId: params.request.pendingRunId,
        inputSlotId,
      });
    } catch (error) {
      console.warn('[task-resume] local slot cleanup failed', {
        pendingRunId: params.request.pendingRunId,
        inputSlotId,
        error,
      });
    }
  }
  return slotIdsToUpload.length === params.missingInputSlotIds.length
    ? 'bound'
    : 'missing_files';
}

/**
 * Recovers a paid local-file task on the browser side: re-uses the Workspace
 * paid upload contract stored with the staged files, binds every recovered
 * file, then asks the server to resume. IndexedDB being empty is not an
 * error when the server already holds every binding.
 */
export async function recoverWorkspaceTaskLocalFiles(params: {
  request: WorkspaceTaskResumeRequest;
  userId: string;
  missingInputSlotIds: string[];
}): Promise<WorkspaceTaskLocalRecoveryOutcome> {
  const binding = await bindMissingWorkspaceTaskLocalFileSlots(params);
  const result = await resumeWorkspaceTaskPayment(params.request);
  if (result.status === 'needs_local_files') {
    return binding === 'retry_later'
      ? { status: 'retry_later' }
      : { status: 'missing_files' };
  }
  return { status: 'completed', result };
}

function recoverWorkspaceTaskLocalFilesOnce(params: {
  request: WorkspaceTaskResumeRequest;
  userId: string;
  missingInputSlotIds: string[];
}): Promise<WorkspaceTaskLocalRecoveryOutcome> {
  const key = `${params.userId}:${params.request.pendingRunId}:${params.request.paymentIntentId}:${params.request.guestRecoveryToken ?? ''}`;
  const existing = localFileRecoveries.get(key);
  if (existing) {
    return existing;
  }
  const pending = recoverWorkspaceTaskLocalFiles(params).finally(() => {
    localFileRecoveries.delete(key);
  });
  localFileRecoveries.set(key, pending);
  return pending;
}

type WorkspaceTaskMissingInputSlot = NonNullable<
  WorkspaceTaskResumeResult['expectedInputSlots']
>[number];

function selectWorkspaceTaskFiles(params: {
  accept: string;
  count: number;
}): Promise<File[] | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = params.accept;
    input.multiple = params.count > 1;
    input.hidden = true;
    const finish = (files: File[] | null) => {
      input.remove();
      resolve(files);
    };
    input.addEventListener(
      'change',
      () => finish(Array.from(input.files ?? [])),
      { once: true }
    );
    input.addEventListener('cancel', () => finish(null), { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

export async function recoverReselectedWorkspaceTaskFiles(params: {
  request: WorkspaceTaskResumeRequest;
  userId: string;
  toolId: string;
  locale: string;
  missingSlots: WorkspaceTaskMissingInputSlot[];
}): Promise<WorkspaceTaskLocalRecoveryOutcome | null> {
  const model = await loadWorkspaceToolModel({
    toolId: params.toolId,
    locale: params.locale,
    surface: 'launched-runtime',
  });
  const tool = model.initialAction.toolViews.find(
    (candidate) => candidate.toolId === params.toolId
  );
  if (!tool || params.missingSlots.length === 0) {
    throw new Error('Purchased task upload contract is unavailable.');
  }
  const files = await selectWorkspaceTaskFiles({
    accept: tool.runtimeCaps.accept,
    count: params.missingSlots.length,
  });
  if (!files) return null;
  if (files.length !== params.missingSlots.length) {
    throw new Error(
      siteRuntimeTaskPaymentResumeCopy.fileCountMismatch.replace(
        '{count}',
        String(params.missingSlots.length)
      )
    );
  }
  const imageDimensionLimits = {
    ...(tool.runtimeCaps.maxImageWidth
      ? { maxWidth: tool.runtimeCaps.maxImageWidth }
      : {}),
    ...(tool.runtimeCaps.maxImageHeight
      ? { maxHeight: tool.runtimeCaps.maxImageHeight }
      : {}),
    ...(tool.runtimeCaps.maxImageMegapixels
      ? { maxMegapixels: tool.runtimeCaps.maxImageMegapixels }
      : {}),
  };
  const staged = await stageWorkspaceTaskLocalFiles({
    pendingRunId: params.request.pendingRunId,
    userId: params.userId,
    toolId: params.toolId,
    files: params.missingSlots.map((slot, index) => ({
      inputSlotId: slot.inputSlotId,
      file: files[index] as File,
    })),
    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
    uploadContract: {
      maxBytes: tool.runtimeCaps.paidMaxSingleBytes,
      supportedMimeTypes: resolveWorkspaceAcceptedUploadTypes(
        tool.runtimeCaps.accept
      ),
      ...(Object.keys(imageDimensionLimits).length > 0
        ? { imageDimensionLimits }
        : {}),
    },
  });
  if (!staged.staged) {
    throw new Error(siteRuntimeTaskPaymentResumeCopy.localStageFailed);
  }
  return recoverWorkspaceTaskLocalFiles({
    request: params.request,
    userId: params.userId,
    missingInputSlotIds: params.missingSlots.map((slot) => slot.inputSlotId),
  });
}

function presentWorkspaceTaskResumeFeedback(result: WorkspaceTaskResumeResult) {
  const feedback = resolveWorkspaceTaskResumeFeedback(result);
  if (feedback.tone === 'success') {
    toast.success(feedback.message);
  } else if (feedback.tone === 'info') {
    toast.info(feedback.message);
  } else {
    toast.error(feedback.message);
  }
}

/**
 * A blocked result keeps the token in the URL so a later refresh can retry
 * once the grant or access state becomes available; every other terminal
 * status is cleaned up.
 */
function clearWorkspaceTaskResumeResultParamsFromLocation(
  result: WorkspaceTaskResumeResult,
  userId: string
) {
  if (result.status === 'blocked') {
    return;
  }
  const submitted =
    result.status === 'submitted' || result.status === 'already_submitted';
  if (
    submitted &&
    (!result.jobId ||
      !result.toolId ||
      !result.input ||
      !result.originalImageName)
  ) {
    throw new Error('Submitted task is missing its Workspace handoff.');
  }
  window.history.replaceState(
    window.history.state,
    '',
    clearWorkspaceTaskResumeResultParams(window.location.href)
  );
  if (submitted) {
    dispatchCmsWorkspaceLaunch({
      launchId: `task-resume:${result.jobId}`,
      autoSubmit: false,
      defaultToolId: result.toolId!,
      resumeJob: {
        userId,
        jobId: result.jobId!,
        input: result.input!,
        originalImageName: result.originalImageName!,
      },
    });
  }
}

/**
 * Workspace-page owner for the paid task resume. A blocked result keeps the
 * token in the URL so a later refresh can retry once the grant or access
 * state becomes available; every other terminal status is cleaned up.
 */
export function useWorkspaceTaskPaymentResume(locale = 'en') {
  const { status: authStatus, currentUserId } = useAuthSessionObserver();
  // Guest files use the local IndexedDB guest namespace; the server authorizes by signed visitor cookie.
  const taskOwnerKey =
    currentUserId ?? (authStatus === 'unauthenticated' ? 'guest' : null);
  const search = useSearchParams().toString();
  const [localRecovery, setLocalRecovery] = useState<{
    request: WorkspaceTaskResumeRequest;
    ownerKey: string;
    toolId: string;
    missingInputSlotIds: string[];
    missingSlots: WorkspaceTaskMissingInputSlot[];
  } | null>(null);

  useEffect(() => {
    void cleanupExpiredWorkspaceTaskLocalFiles().catch((error) => {
      console.warn('[task-resume] local expiry cleanup failed', { error });
    });
    if (!taskOwnerKey) return;
    const callbackRequest = resolveWorkspaceTaskResumeRequest(`/?${search}`);
    if (callbackRequest?.guestRecoveryToken) {
      rememberGuestTaskCheckoutRecovery({
        pendingRunId: callbackRequest.pendingRunId,
        paymentIntentId: callbackRequest.paymentIntentId,
        guestRecoveryToken: callbackRequest.guestRecoveryToken,
      });
    }
    const request = callbackRequest ?? readLatestGuestTaskCheckoutRecovery();
    if (!request) {
      return;
    }
    let cancelled = false;
    void requestWorkspaceTaskResumeOnce(request, taskOwnerKey)
      .then((result) => {
        if (cancelled) {
          return;
        }
        const ownerKey = resolveWorkspaceTaskOwnerKey(result, taskOwnerKey);
        if (request.guestRecoveryToken && result.status !== 'blocked') {
          clearGuestRecoveryTokenFromLocation();
          if (
            result.status === 'submitted' ||
            result.status === 'already_submitted'
          ) {
            forgetGuestTaskCheckoutRecovery(request.pendingRunId);
          }
        }
        if (result.status === 'needs_local_files') {
          const missingInputSlotIds = resolveMissingInputSlotIds(result);
          const missingSlots = (result.expectedInputSlots ?? []).filter(
            (slot) => missingInputSlotIds.includes(slot.inputSlotId)
          );
          if (
            !result.toolId ||
            missingSlots.length !== missingInputSlotIds.length
          ) {
            toast.error(siteRuntimeTaskPaymentResumeCopy.requestFailed);
            return;
          }
          setLocalRecovery({
            request,
            ownerKey,
            toolId: result.toolId,
            missingInputSlotIds,
            missingSlots,
          });
          return;
        }
        presentWorkspaceTaskResumeFeedback(result);
        clearWorkspaceTaskResumeResultParamsFromLocation(result, ownerKey);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        toast.error(siteRuntimeTaskPaymentResumeCopy.requestFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [authStatus, taskOwnerKey, search]);

  useEffect(() => {
    if (
      resolveWorkspaceTaskResumeRequest(window.location.href) ||
      readLatestGuestTaskCheckoutRecovery() ||
      !taskOwnerKey
    ) {
      return;
    }
    let cancelled = false;
    void findLatestResumableWorkspaceTaskPayment()
      .then((pending) => {
        if (cancelled || !pending) return;
        toast.info(siteRuntimeTaskPaymentResumeCopy.pendingAvailable, {
          duration: Number.POSITIVE_INFINITY,
          id: `workspace-task-resume:${pending.pendingRunId}`,
          action: {
            label: siteRuntimeTaskPaymentResumeCopy.continueTask,
            onClick: () => {
              const request = {
                pendingRunId: pending.pendingRunId,
                paymentIntentId: pending.paymentIntentId,
              };
              void requestWorkspaceTaskResumeOnce(request, taskOwnerKey)
                .then((result) => {
                  const ownerKey = resolveWorkspaceTaskOwnerKey(
                    result,
                    taskOwnerKey
                  );
                  if (result.status !== 'needs_local_files') {
                    presentWorkspaceTaskResumeFeedback(result);
                    clearWorkspaceTaskResumeResultParamsFromLocation(
                      result,
                      ownerKey
                    );
                    return;
                  }
                  const missingInputSlotIds =
                    resolveMissingInputSlotIds(result);
                  const missingSlots = (result.expectedInputSlots ?? []).filter(
                    (slot) => missingInputSlotIds.includes(slot.inputSlotId)
                  );
                  if (
                    !result.toolId ||
                    missingSlots.length !== missingInputSlotIds.length
                  ) {
                    toast.error(siteRuntimeTaskPaymentResumeCopy.requestFailed);
                    return;
                  }
                  setLocalRecovery({
                    request,
                    ownerKey,
                    toolId: result.toolId,
                    missingInputSlotIds,
                    missingSlots,
                  });
                })
                .catch(() =>
                  toast.error(siteRuntimeTaskPaymentResumeCopy.requestFailed)
                );
            },
          },
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [authStatus, taskOwnerKey]);

  useEffect(() => {
    if (!localRecovery) {
      return;
    }
    let cancelled = false;
    void recoverWorkspaceTaskLocalFilesOnce({
      request: localRecovery.request,
      userId: localRecovery.ownerKey,
      missingInputSlotIds: localRecovery.missingInputSlotIds,
    })
      .then((outcome) => {
        if (cancelled) {
          return;
        }
        if (outcome.status === 'retry_later') {
          toast.error(siteRuntimeTaskPaymentResumeCopy.uploadRetry);
          return;
        }
        if (outcome.status === 'missing_files') {
          toast.info(siteRuntimeTaskPaymentResumeCopy.needsLocalFiles, {
            duration: Number.POSITIVE_INFINITY,
            id: `workspace-task-reselect:${localRecovery.request.pendingRunId}`,
            action: {
              label: siteRuntimeTaskPaymentResumeCopy.selectFiles,
              onClick: () => {
                void recoverReselectedWorkspaceTaskFiles({
                  request: localRecovery.request,
                  userId: localRecovery.ownerKey,
                  toolId: localRecovery.toolId,
                  locale,
                  missingSlots: localRecovery.missingSlots,
                })
                  .then((selectedOutcome) => {
                    if (!selectedOutcome) return;
                    if (selectedOutcome.status === 'completed') {
                      if (localRecovery.request.guestRecoveryToken) {
                        forgetGuestTaskCheckoutRecovery(
                          localRecovery.request.pendingRunId
                        );
                      }
                      presentWorkspaceTaskResumeFeedback(
                        selectedOutcome.result
                      );
                      clearWorkspaceTaskResumeResultParamsFromLocation(
                        selectedOutcome.result,
                        localRecovery.ownerKey
                      );
                      setLocalRecovery(null);
                      return;
                    }
                    toast.error(siteRuntimeTaskPaymentResumeCopy.uploadRetry);
                  })
                  .catch((error) =>
                    toast.error(
                      error instanceof Error && error.message.trim()
                        ? error.message
                        : siteRuntimeTaskPaymentResumeCopy.uploadRetry
                    )
                  );
              },
            },
          });
          return;
        }
        if (localRecovery.request.guestRecoveryToken) {
          forgetGuestTaskCheckoutRecovery(localRecovery.request.pendingRunId);
        }
        presentWorkspaceTaskResumeFeedback(outcome.result);
        clearWorkspaceTaskResumeResultParamsFromLocation(
          outcome.result,
          localRecovery.ownerKey
        );
        setLocalRecovery(null);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        toast.error(siteRuntimeTaskPaymentResumeCopy.requestFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [localRecovery, locale]);
}
